import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { Gift, Upload, Save, X } from 'lucide-react';

export default function OfferConfigPage() {
  const [config, setConfig] = useState({
    offerEnabled: false,
    offerText: '',
    offerImage: '',
  });
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    axios.get('/api/payment/admin-payment-config')
      .then(r => {
        const d = r.data || {};
        setConfig({
          offerEnabled: d.offerEnabled || false,
          offerText: d.offerText || '',
          offerImage: d.offerImage || '',
        });
      })
      .catch(() => {
        toast.error('Failed to load offer config');
      });
  }, []);

  const handleOfferImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      return toast.error('Offer image must be under 2MB');
    }
    const reader = new FileReader();
    reader.onloadend = () => setConfig(prev => ({ ...prev, offerImage: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.post('/api/payment/admin-payment-config', config);
      toast.success('Offer settings saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save offer settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-8">
        <div className="flex flex-col gap-3 mb-2">
          <div>
            <h1 className="text-2xl font-bold text-white">Offer Popup Settings</h1>
            <p className="text-gray-400 mt-1">Manage the special popup offer shown to users when they login or refresh.</p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.4fr_0.9fr]">
          <div className="bg-dark-700 border border-white/10 rounded-3xl p-6 space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary-500/10 flex items-center justify-center text-primary-400">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white text-lg font-semibold">Special Offer Popup</p>
                <p className="text-gray-500 text-sm">Enable the popup and configure the content shown to users.</p>
              </div>
            </div>

            <div className="bg-dark-600 border border-white/10 rounded-3xl p-5">
              <div className="flex items-center justify-between gap-4 mb-5">
                <div>
                  <p className="text-sm text-white font-medium">Popup Status</p>
                  <p className="text-gray-500 text-xs">Turn the special offer on or off.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfig(prev => ({ ...prev, offerEnabled: !prev.offerEnabled }))}
                  className={`relative inline-flex items-center h-9 rounded-full w-16 transition-colors ${config.offerEnabled ? 'bg-primary-500' : 'bg-dark-500 border border-white/10'}`}>
                  <span className={`absolute left-1 top-1 w-7 h-7 rounded-full bg-white shadow transition-transform ${config.offerEnabled ? 'translate-x-7' : 'translate-x-0'}`} />
                </button>
              </div>

              <div className="space-y-3">
                <label className="block text-xs text-gray-400">Offer Text</label>
                <textarea
                  value={config.offerText}
                  onChange={e => setConfig(prev => ({ ...prev, offerText: e.target.value }))}
                  className="w-full min-h-[180px] bg-dark-800 border border-white/10 rounded-3xl px-4 py-4 text-sm text-white focus:outline-none focus:border-primary-500 resize-none"
                  placeholder="Write the offer message shown to users..."
                />
                <p className="text-gray-500 text-xs">Enter the promotional text for the popup. Keep it short and clear.</p>
              </div>
            </div>

            <div className="bg-dark-600 border border-white/10 rounded-3xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-white font-medium">Offer Preview</p>
                  <p className="text-gray-500 text-xs">Preview how the user popup content will appear.</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${config.offerEnabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-gray-600/30 text-gray-300'}`}>
                  {config.offerEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="rounded-3xl bg-dark-700 border border-white/5 p-5">
                <p className="text-sm text-gray-300 mb-4">{config.offerText || 'No offer text configured yet.'}</p>
                {config.offerImage && (
                  <div className="rounded-3xl overflow-hidden border border-white/10 bg-dark-800">
                    <img src={config.offerImage} alt="Offer preview" className="w-full h-40 object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-dark-700 border border-white/10 rounded-3xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-semibold">Offer Image</p>
                  <p className="text-gray-500 text-sm">Upload an optional image for the popup.</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="w-full rounded-3xl border border-white/10 bg-dark-600 h-64 overflow-hidden flex items-center justify-center">
                  {config.offerImage ? (
                    <img src={config.offerImage} alt="Offer" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center px-4">
                      <p className="text-gray-500 text-sm">No image selected</p>
                      <p className="text-gray-500 text-xs mt-2">Recommended size: 800x600. JPG/PNG under 2MB.</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 text-white rounded-2xl px-4 py-3 text-sm font-medium transition-colors"
                  >
                    <Upload className="w-4 h-4" /> Upload Image
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleOfferImageUpload} className="hidden" />
                  {config.offerImage && (
                    <button
                      type="button"
                      onClick={() => setConfig(prev => ({ ...prev, offerImage: '' }))}
                      className="text-amber-400 hover:text-amber-300 text-sm"
                    >
                      Remove image
                    </button>
                  )}
                  <p className="text-gray-500 text-xs">Optional image shown in the popup. If you don’t want an image, leave this blank.</p>
                </div>
              </div>
            </div>

            <div className="bg-dark-700 border border-white/10 rounded-3xl p-6">
              <p className="text-gray-400 text-sm mb-4">Save your offer settings once you are ready. The popup will be shown immediately after the next user login/refresh if enabled.</p>
              <button
                onClick={handleSave}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold py-3 rounded-2xl transition-colors"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <><Save className="w-4 h-4" /> Save Offer Settings</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
